import { Box, Chip, Paper, Typography } from '@mui/material';
import { FC, useEffect, useMemo } from 'react';
import { BaseScreen } from '../../../Components/Templates/BaseScreen/basescreen.component';
import { BaseContentPage } from '../../../Components/Templates/BaseContentPage/base-content-page.componen';
import { Button } from '../../../Components/Form/Button/button.scomponent';
import { GoPlus } from "react-icons/go";
import { Table } from '../../../Components/Table/table.component';
import { Link, useNavigate } from 'react-router-dom';
import { useListPricePolicies } from './policies-price-list.hook';
import { Placeholder } from '../../../Components/Placeholder/placeholder.component';
import { TableListSkeleton } from '../../../Components/Table/table-list-skeleton.component';
import { Can } from '../../../Components/Can';
import { ButtonActionMenu } from '../../../Components/Form/ButtonActionsMenu/button-action-menu.component';
import { FaEdit, FaSync, FaTrash } from 'react-icons/fa';
import { ConfirmDialog } from '../../../Components/Dialog/variants/dialog-confirmation/dialog-confirmation.component';
import { ListPricePoliciesUseCase } from '../../../Usecase/PricePolicies/list-price-policies-usecase';
import { DestroyPricePoliciesUseCase } from '../../../Usecase/PricePolicies/destroy-price-policies-usecase';
import { ResyncPricePoliciesUseCase } from '../../../Usecase/PricePolicies/resync-price-policies-usecase';

interface IPricePoliciesList {
  listPricePoliciesUseCase: ListPricePoliciesUseCase
  destroyPricePoliciesUseCase: DestroyPricePoliciesUseCase
  resyncPricePoliciesUseCase: ResyncPricePoliciesUseCase
}

const PricePoliciesList : FC<IPricePoliciesList> = ({
  listPricePoliciesUseCase,
  destroyPricePoliciesUseCase,
  resyncPricePoliciesUseCase,
}) => {
    const navigate = useNavigate()

    const {
      totalRegisters,
      loading,
      loadError,
      handleDestroyPricePolicy,
      pricePolicyToDestroy,
      setPricePolicyToDestroy,
      list,
      listPolicies,
      pricePolicyToResync,
      setPricePolicyToResync,
      handleResyncPricePolicy,
      page,
      currentSearch,
      handleSearch,
      handlePageChange,
    } = useListPricePolicies({
        listPricePoliciesUseCase,
        destroyPricePoliciesUseCase,
        resyncPricePoliciesUseCase,
    })

    useEffect(() => {
      listPolicies({ page: 1, searchBy: '' })
    }, [listPolicies])

    const columns = useMemo(() => {
        const createColumns: any[] = [
          {
            field: 'name',
            headerName: 'Nome',
            minWidth: 180,
            flex: 1,
            renderCell: (params: any) => (
              <Box
                component={Link}
                to={`/price-policies-form/${params.row.id}`}
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  textDecoration: 'none',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  lineHeight: 1.35,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {params.value || '—'}
              </Box>
            ),
          },
          {
            field: 'description',
            headerName: 'Descrição',
            minWidth: 240,
            flex: 2,
            sortable: false,
            renderCell: (params: any) => (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  lineHeight: 1.35,
                }}
              >
                {params.value || '—'}
              </Typography>
            ),
          },
          {
            field: 'rulesCount',
            headerName: 'Regras',
            width: 100,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => (
              <Chip
                label={params.value === 1 ? '1 regra' : `${params.value ?? 0} regras`}
                size="small"
                variant={params.value > 0 ? 'filled' : 'outlined'}
                color={params.value > 0 ? 'primary' : 'default'}
              />
            ),
          },
          {
            field: 'isActive',
            headerName: 'Status',
            width: 100,
            sortable: false,
            renderCell: (params: any) => (
              <Chip
                label={params.value ? 'Ativa' : 'Inativa'}
                size="small"
                color={params.value ? 'success' : 'default'}
                variant={params.value ? 'filled' : 'outlined'}
              />
            ),
          },
          {
            field: "actions",
            headerName: "Ações",
            width: 80,
            sortable: false,
            renderCell: (params: any) => (
              <ButtonActionMenu
                options={[
                  {
                    title: 'Editar',
                    onClick: () => navigate(`/price-policies-form/${params.row.id}`),
                    icon: <FaEdit size={18} />,
                  },
                  {
                    title: 'Re-sincronizar produtos',
                    onClick: () => setPricePolicyToResync(params.row),
                    icon: <FaSync size={18} />,
                  },
                  {
                    title: 'Excluir',
                    onClick: () => setPricePolicyToDestroy(params.row),
                    titleColor: 'error.main',
                    icon: <FaTrash size={18} />,
                  },
                ]}
              />
            ),
          },
        ];

        return createColumns;
      }, [navigate, setPricePolicyToDestroy, setPricePolicyToResync]);

    return (
      <Can permissions={['menu:products:manage']}>
        <ConfirmDialog
          title="Remover política de preços"
          onCancel={() => setPricePolicyToDestroy(undefined)}
          description={`Tem certeza que deseja remover "${pricePolicyToDestroy?.name || 'esta política'}"?`}
          open={!!pricePolicyToDestroy}
          onConfirm={handleDestroyPricePolicy}
        />
        <ConfirmDialog
          title="Re-sincronizar produtos"
          onCancel={() => setPricePolicyToResync(undefined)}
          description={`Tem certeza que deseja re-sincronizar os preços de todos os produtos vinculados à política "${pricePolicyToResync?.name || 'selecionada'}"?`}
          open={!!pricePolicyToResync}
          onConfirm={handleResyncPricePolicy}
        />
        <BaseScreen>
            <Paper sx={{ borderRadius: 2, overflow: 'hidden', width: '100%' }}>
                <BaseContentPage
                    title="Políticas de Preços"
                    subtitle="Gerencie políticas de desconto por quantidade e regras de preço"
                    minHeight="auto"
                    search
                    searchDefaultValue={currentSearch}
                    searchFunction={handleSearch}
                    searchLoading={loading && !!list?.length}
                    placeholderSearch="Pesquisar por nome ou descrição"
                    breadcrumb={[
                      { title: 'Políticas de Preços', link: "/price-policies-list" },
                      { title: 'Listagem' },
                    ]}
                    Action={() => (
                      <Button
                        LeftIcon={() => <GoPlus size={18} />}
                        sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 130 }, flexShrink: 0 }}
                        boxLeftSx={{ gap: '10px', marginLeft: '5px' }}
                        textSx={{ textTransform: 'Capitalize', fontWeight: 500, whiteSpace: 'nowrap' }}
                        onClick={() => navigate('/price-policies-form')}
                      >
                        Adicionar
                      </Button>
                    )}
                >
                  {!!list?.length ? (
                    <Box sx={{ width: '100%', minWidth: 0 }}>
                      <Table
                        data={list}
                        columns={columns}
                        totalCount={totalRegisters}
                        search={currentSearch}
                        page={page}
                        onPageChange={handlePageChange}
                        rowHeight={64}
                        stickyHeader
                        maxHeight="calc(100vh - 280px)"
                        sx={{ width: '100%' }}
                      />
                    </Box>
                  ) : loading ? (
                    <TableListSkeleton rowHeight={64} />
                  ) : loadError ? (
                    <Placeholder
                      description="Não foi possível carregar as políticas de preço."
                      onRetry={() => listPolicies({ page, searchBy: currentSearch })}
                    />
                  ) : (
                    <Placeholder description="Nenhuma política de preços encontrada." />
                  )}
                </BaseContentPage>
            </Paper>
        </BaseScreen>
        </Can>
    )
}

export { PricePoliciesList };
